export type WochentagKey = 'montag' | 'dienstag' | 'mittwoch' | 'donnerstag' | 'freitag' | 'samstag' | 'sonntag';

export type ArbeitsRhythmus = 'woechentlich' | 'monatlich';

export interface ArbeitszeitTagEntry {
    id: string;
    wochentag: WochentagKey;
    von: string;
    bis: string;
    pauseMinuten: string;
    /** wöchentlich = jede Woche; monatlich = nur X Tage pro Monat (Durchschnitt über Wochensatz) */
    rhythmus: ArbeitsRhythmus;
    tageProMonat: string;
}

export const WOCHENTAG_OPTIONS: { key: WochentagKey; label: string }[] = [
    { key: 'montag', label: 'Montag' },
    { key: 'dienstag', label: 'Dienstag' },
    { key: 'mittwoch', label: 'Mittwoch' },
    { key: 'donnerstag', label: 'Donnerstag' },
    { key: 'freitag', label: 'Freitag' },
    { key: 'samstag', label: 'Samstag' },
    { key: 'sonntag', label: 'Sonntag' },
];

const WOCHENTAG_LABEL: Record<WochentagKey, string> = Object.fromEntries(
    WOCHENTAG_OPTIONS.map(({ key, label }) => [key, label])
) as Record<WochentagKey, string>;

export const createArbeitszeitEntry = (wochentag: WochentagKey = 'montag'): ArbeitszeitTagEntry => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    wochentag,
    von: '',
    bis: '',
    pauseMinuten: '',
    rhythmus: 'woechentlich',
    tageProMonat: '',
});

export const minutesToTime = (totalMinutes: number): string => {
    const day = 24 * 60;
    const wrapped = ((totalMinutes % day) + day) % day;
    const hours = Math.floor(wrapped / 60);
    const minutes = wrapped % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const shiftTimeValue = (value: string, deltaMinutes: number, fallback = '09:00'): string => {
    const current = parseTimeToMinutes(value);
    const base = current === null ? parseTimeToMinutes(fallback) ?? 9 * 60 : current;
    return minutesToTime(base + deltaMinutes);
};

export const nextWochentag = (key: WochentagKey): WochentagKey => {
    const index = WOCHENTAG_OPTIONS.findIndex((opt) => opt.key === key);
    const nextIndex = index < 0 ? 0 : (index + 1) % WOCHENTAG_OPTIONS.length;
    return WOCHENTAG_OPTIONS[nextIndex].key;
};

export const createArbeitszeitEntryFromPrevious = (previous?: ArbeitszeitTagEntry): ArbeitszeitTagEntry => {
    if (!previous) {
        return {
            ...createArbeitszeitEntry('montag'),
            von: '09:00',
            bis: '18:00',
            pauseMinuten: '30',
        };
    }
    return {
        ...createArbeitszeitEntry(nextWochentag(previous.wochentag)),
        von: previous.von || '09:00',
        bis: previous.bis || '18:00',
        pauseMinuten: previous.pauseMinuten || '30',
        rhythmus: previous.rhythmus,
        tageProMonat: previous.tageProMonat,
    };
};

export const parseTimeToMinutes = (value: string): number | null => {
    const match = value.trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours > 23 || minutes >= 60) return null;
    return hours * 60 + minutes;
};

/** Erlaubt 9:00, 09:00, 9.00, 900, 0900 – für freie Zahleneingabe statt Native-Time-Picker. */
export const normalizeTypedTime = (value: string): string | null => {
    const raw = value.trim().replace(',', ':').replace('.', ':').replace(/\s/g, '');
    if (!raw) return '';
    const colon = raw.match(/^(\d{1,2}):(\d{1,2})$/);
    if (colon) {
        const hours = Number(colon[1]);
        const minutes = Number(colon[2]);
        if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
            return minutesToTime(hours * 60 + minutes);
        }
        return null;
    }
    if (/^\d{1,2}$/.test(raw)) {
        const hours = Number(raw);
        if (hours >= 0 && hours <= 23) return minutesToTime(hours * 60);
        return null;
    }
    if (/^\d{3,4}$/.test(raw)) {
        const padded = raw.padStart(4, '0');
        const hours = Number(padded.slice(0, 2));
        const minutes = Number(padded.slice(2));
        if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
            return minutesToTime(hours * 60 + minutes);
        }
    }
    return null;
};

export const calculateTagesstunden = (entry: ArbeitszeitTagEntry): number => {
    const vonMin = parseTimeToMinutes(entry.von);
    const bisMin = parseTimeToMinutes(entry.bis);
    if (vonMin === null || bisMin === null || bisMin <= vonMin) return 0;
    const pause = Number(entry.pauseMinuten);
    const pauseMin = Number.isFinite(pause) && pause > 0 ? pause : 0;
    const netMin = Math.max(0, bisMin - vonMin - pauseMin);
    return Math.round((netMin / 60) * 100) / 100;
};

export const calculateWochenstundenFromPlan = (plan: ArbeitszeitTagEntry[], wochensatz: number): number => {
    if (!Number.isFinite(wochensatz) || wochensatz <= 0) return 0;
    const complete = plan.filter(isArbeitszeitEntryComplete);
    if (complete.length === 0) return 0;

    let sum = 0;
    for (const entry of complete) {
        const tagesStd = calculateTagesstunden(entry);
        if (entry.rhythmus === 'monatlich') {
            const raw = Number(entry.tageProMonat);
            const tageMonat = Number.isFinite(raw) ? Math.min(3, Math.max(1, Math.round(raw))) : 1;
            sum += (tagesStd * tageMonat) / wochensatz;
        } else {
            sum += tagesStd;
        }
    }
    return Math.round(sum * 100) / 100;
};

export const formatTimeForDocument = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return '____';
    const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return trimmed;
    return `${match[1].padStart(2, '0')}:${match[2]} Uhr`;
};

export const formatPauseForDocument = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return '0 Min.';
    const num = Number(trimmed);
    if (!Number.isFinite(num) || num <= 0) return '0 Min.';
    return num === 1 ? '1 Minute' : `${num} Minuten`;
};

export const formatRhythmusForDocument = (entry: ArbeitszeitTagEntry): string => {
    if (entry.rhythmus === 'monatlich') {
        const raw = Number(entry.tageProMonat);
        const tage = Number.isFinite(raw) ? Math.min(3, Math.max(1, Math.round(raw))) : 1;
        return `${tage} Tag(e) pro Monat (Ø-Wochenstunden)`;
    }
    return 'Wöchentlich';
};

export const formatArbeitsplanZeile = (entry: ArbeitszeitTagEntry): string => {
    const tag = WOCHENTAG_LABEL[entry.wochentag] || entry.wochentag;
    const rhythmus = entry.rhythmus === 'monatlich' ? `, ${formatRhythmusForDocument(entry)}` : '';
    return `${tag}: ${formatTimeForDocument(entry.von)} bis ${formatTimeForDocument(entry.bis)} (Pause: ${formatPauseForDocument(entry.pauseMinuten)})${rhythmus}`;
};

export const sortArbeitsplanByWochentag = (plan: ArbeitszeitTagEntry[]): ArbeitszeitTagEntry[] => {
    const order = WOCHENTAG_OPTIONS.map((d) => d.key);
    return [...plan].sort((a, b) => order.indexOf(a.wochentag) - order.indexOf(b.wochentag));
};

export const isValidTimeValue = (value: string): boolean => /^\d{1,2}:\d{2}$/.test(value.trim());

export const isArbeitszeitEntryComplete = (entry: ArbeitszeitTagEntry): boolean => {
    if (!isValidTimeValue(entry.von) || !isValidTimeValue(entry.bis)) return false;
    if (calculateTagesstunden(entry) <= 0) return false;
    if (entry.rhythmus === 'monatlich') {
        const raw = Number(entry.tageProMonat);
        return Number.isFinite(raw) && raw >= 1 && raw <= 3;
    }
    return true;
};

/** Reguläre Betriebsarbeitstage/Jahr nach BAG-Standard (5×52 bzw. 6×52). */
export const betriebArbeitstageProJahr = (wochentageProWoche: 5 | 6): number =>
    wochentageProWoche === 6 ? 312 : 260;

/** Tatsächliche Jahresarbeitstage aus Arbeitsplan (wöchentlich × 52, monatlich × 12). */
export const calculateJahresarbeitstageFromPlan = (plan: ArbeitszeitTagEntry[]): number => {
    const complete = plan.filter(isArbeitszeitEntryComplete);
    if (complete.length === 0) return 0;

    const weeklyDays = new Set(
        complete.filter((entry) => entry.rhythmus === 'woechentlich').map((entry) => entry.wochentag)
    );
    let sum = weeklyDays.size * 52;

    for (const entry of complete) {
        if (entry.rhythmus !== 'monatlich') continue;
        const raw = Number(entry.tageProMonat);
        const tageMonat = Number.isFinite(raw) ? Math.min(3, Math.max(1, Math.round(raw))) : 1;
        sum += tageMonat * 12;
    }

    return sum;
};

/** § 5 Abs. 2 BUrlG: Bruchteile ≥ 0,5 werden auf volle Tage aufgerundet. */
export const roundUrlaubstageNachBurlg = (raw: number): number => {
    if (!Number.isFinite(raw) || raw <= 0) return 0;
    const whole = Math.floor(raw);
    const fraction = raw - whole;
    if (fraction >= 0.5) return whole + 1;
    return whole;
};

/** BAG-Urlaubsformel: (Vollzeit-Urlaub / Betriebs-Jahresarbeitstage) × individuelle Jahresarbeitstage. */
export const calculateUrlaubstageNachBag = (
    urlaubVollzeit: number,
    betriebJahresarbeitstage: number,
    mitarbeiterJahresarbeitstage: number
): number => {
    if (urlaubVollzeit <= 0 || betriebJahresarbeitstage <= 0 || mitarbeiterJahresarbeitstage <= 0) return 0;
    const raw = (urlaubVollzeit / betriebJahresarbeitstage) * mitarbeiterJahresarbeitstage;
    return roundUrlaubstageNachBurlg(raw);
};

/** @deprecated Wochen-Ø – für Urlaub bitte calculateJahresarbeitstageFromPlan + calculateUrlaubstageNachBag nutzen. */
export const calculateArbeitstageProWocheFromPlan = (
    plan: ArbeitszeitTagEntry[],
    wochensatz: number
): number => {
    const jahres = calculateJahresarbeitstageFromPlan(plan);
    if (jahres <= 0 || !Number.isFinite(wochensatz) || wochensatz <= 0) return 0;
    return Math.round((jahres / wochensatz) * 100) / 100;
};

/** @deprecated Alias */
export const countArbeitstageProWocheFromPlan = (plan: ArbeitszeitTagEntry[]): number =>
    calculateArbeitstageProWocheFromPlan(plan, 4.33);

export const formatStdForDisplay = (value: number): string => {
    if (!Number.isFinite(value) || value <= 0) return '';
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(2).replace('.', ',');
};

export const round2 = (value: number): number => Math.round(value * 100) / 100;

export const calculateBruttoFromStundenlohn = (
    stundenlohn: number,
    wochenstunden: number,
    wochensatz: number
): number => {
    const monatsstunden = wochenstunden * wochensatz;
    if (monatsstunden <= 0) return 0;
    return round2(stundenlohn * monatsstunden);
};

export const calculateStundenlohnFromBrutto = (
    brutto: number,
    wochenstunden: number,
    wochensatz: number
): number => {
    const monatsstunden = wochenstunden * wochensatz;
    if (monatsstunden <= 0) return 0;
    return round2(brutto / monatsstunden);
};
